import { useEffect, useMemo, useRef, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { AssetClassMappingWithIndex, filterMappingsBySearch } from '../AssetClasses.meta';

const SEARCH_DEBOUNCE_MS = 300;

export const useDebouncedFilterMappingsBySearch = (
  mappings: AssetClassMappingWithIndex[],
  persistedSearchQuery: string,
  onSearchQueryPersist: (query: string) => void,
) => {
  const [searchQuery, setSearchQuery] = useState(persistedSearchQuery);
  const [debouncedSearchQuery] = useDebounce(searchQuery, SEARCH_DEBOUNCE_MS);
  const searchQueryRef = useRef(searchQuery);

  searchQueryRef.current = searchQuery;

  const isSearchPending = searchQuery !== debouncedSearchQuery;
  const filterQuery = isSearchPending ? searchQuery : debouncedSearchQuery;

  const filteredMappings = useMemo(
    () => filterMappingsBySearch(mappings, filterQuery),
    [mappings, filterQuery],
  );

  const hasNoSearchResults =
    !isSearchPending && filterQuery.trim().length > 0 && filteredMappings.length === 0;

  useEffect(() => {
    if (!isSearchPending) {
      onSearchQueryPersist(searchQuery);
    }
  }, [isSearchPending, searchQuery, onSearchQueryPersist]);

  useEffect(
    () => () => {
      onSearchQueryPersist(searchQueryRef.current);
    },
    [onSearchQueryPersist],
  );

  return {
    searchQuery,
    setSearchQuery,
    filteredMappings,
    hasNoSearchResults,
  };
};
